import { Ref, useCallback, useImperativeHandle } from "react";
import { useTranslation } from "@coworker/locales";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import TextModal from "@coworker/apprestructured/src/task-management/components/TextModal/TextModal";
import ExpeditTag from "@coworker/app/src/assets/svg/EXPEDIT-tag.svg?react";
import SupplierNumberMarker from "@coworker/app/src/assets/svg/supplier-number-marker.svg?react";

const SUPPLIER_NUMBER_LENGTH = 5;

const validateSupplierNumber = (value: string) =>
  /^\d+$/.test(value) && value.length === SUPPLIER_NUMBER_LENGTH;

export type SupplierNumberCreatorFragmentConfigProps = {
  field: string;
};

type SupplierNumberCreatorFragmentProps = {
  configProps: SupplierNumberCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function SupplierNumberCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SupplierNumberCreatorFragmentProps) {
  const { field } = configProps;
  const { blockInput, task, onTaskChange } = commonProps;
  const { t } = useTranslation();

  const supplierNumber = getTypedValue<string>(getByPath(task, field));

  useImperativeHandle(ref, () => ({
    valid: isLoading(supplierNumber)
      ? false
      : validateSupplierNumber(supplierNumber ?? ""),
  }));

  const handleConfirmClick = useCallback(
    (newValue: string) => {
      onTaskChange(setByPath(field, newValue));
    },
    [field, onTaskChange]
  );

  if (isLoading(supplierNumber)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <TextModal
      title={t("supplierNumberString")}
      text={supplierNumber ?? ""}
      onConfirmClick={handleConfirmClick}
      disabled={blockInput}
      maxLength={SUPPLIER_NUMBER_LENGTH}
      inputLabel={t("supplierNumberString")}
      inputMode="numeric"
      numericOnly
      validate={validateSupplierNumber}
      inputHintText={t("maxNDigits", { length: SUPPLIER_NUMBER_LENGTH })}
      informationTitle={t("whereCanIfindSupplierString")}
      informationBody={
        <>
          <ExpeditTag />
          <SupplierNumberMarker />
        </>
      }
    />
  );
}

export default SupplierNumberCreatorFragment;
